/**
 * useSectionBg — returns the resolved background color for a given section key.
 * Falls back to the page background if the section is set to "inherit".
 */
import { useDesignState } from '@/hooks/useDesignSystem';
import { PAGE_BG_PRESETS } from '@/pages/admin/components/design/designConstants';
import { DesignSettings, SectionBgConfig } from '@/pages/admin/components/design/designTypes';

const SECTION_BG_COLORS: Record<string, string> = {
  'black':       '#0a0a0a',
  'dark-gray':   '#111827',
  'charcoal':    '#1c1c1e',
  'deep-navy':   '#0d1117',
  'dark-green':  '#0a1a0f',
  'dark-wine':   '#1a0a0a',
  'dark-purple': '#0f0a1a',
  'off-white':   '#f8f7f4',
  'warm-white':  '#faf8f5',
  'light-gray':  '#f3f4f6',
  'cream':       '#fdf6ec',
};

function resolvePageBg(design: DesignSettings): string {
  const preset = PAGE_BG_PRESETS.find(b => b.id === (design.pageBgId ?? 'black')) ?? PAGE_BG_PRESETS[0];
  return design.pageBgId === 'custom'
    ? (design.pageBgCustomColor || '#0a0a0a')
    : preset.color;
}

function resolveSectionBg(cfg: SectionBgConfig | undefined, pageBg: string): string {
  if (!cfg || cfg.bgId === 'inherit') return pageBg;
  if (cfg.bgId === 'custom') return cfg.customColor || pageBg;
  return SECTION_BG_COLORS[cfg.bgId] ?? pageBg;
}

export function isLightColor(hex: string): boolean {
  const h = hex.replace('#', '');
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Perceived luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

type SectionKey = 'sectionProducts' | 'sectionDevices' | 'sectionReviews' | 'sectionContact';

export function useSectionBg(sectionKey: SectionKey): { bg: string; isLight: boolean } {
  const { design } = useDesignState();
  const pageBg = resolvePageBg(design);
  const cfg = design[sectionKey] as SectionBgConfig | undefined;
  const bg = resolveSectionBg(cfg, pageBg);
  return { bg, isLight: isLightColor(bg) };
}
